import {
  Avatar,
  AvatarGroup,
  Box,
  Card,
  Chip,
  LinearProgress,
  Tooltip,
} from '@mui/material';
import Kpi from './Kpi';
import { Event, ScheduleOutlined, TrendingUp } from '@mui/icons-material';
import ListCard from '@/components/datagrid/ListCard';
import PartySelector from '@/components/selectors/PartySelector';
import { useNavigate, useRouteLoaderData } from 'react-router-dom';
import { useMemo, useState } from 'react';
import StatusFilter from './StatusFilter';
import UserSelector from '@/components/selectors/UserSelector';

const projects = [
  {
    _id: 1,
    title: 'Projekt A',
    status: 'active',
    owner: '6772997a8194537ef3121e2d',
    customerName: 'Auto Levy',
    orderNumber: 'AU-12345',
    plannedHours: 120,
    bookedHours: 45,
    billedHours: 30,
    team: ['67627c943efeb85a669259ee', '647eefc735edfc0d3ebe682f'],
  },
  {
    _id: 2,
    title: 'Projekt B',
    status: 'pending',
    owner: '647eefc735edfc0d3ebe682f',
    customerName: 'Autohaus Eifel-Mosel',
    orderNumber: 'AU-67890',
    plannedHours: 98,
    bookedHours: 0,
    billedHours: 0,
    team: ['66da9cbf72a965af92883391'],
  },
];

const statusTitles = {
  pending: 'In Planung',
  active: 'Aktiv',
  completed: 'Abgeschlossen',
};

const statusColors = {
  pending: 'warning',
  active: 'success',
  completed: 'info',
};

const columns = [
  {
    field: 'customerName',
    headerName: 'Kunde',
    flex: 1,
    renderCell: (params) => (
      <Box sx={{ fontWeight: 'bold' }}>{params.value}</Box>
    ),
    filterable: false,
    searchable: false,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: 'title',
    headerName: 'Projektname',
    flex: 1,
    filterable: false,
    searchable: false,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: 'orderNumber',
    headerName: 'Auftragsnummer',
    flex: 1,
    filterable: false,
    searchable: false,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: 'status',
    headerName: 'Status',
    width: 150,
    renderCell: (params) => {
      if (params.value !== 'active') {
        return (
          <Chip
            label={statusTitles[params.value] || 'Unbekannt'}
            color={statusColors[params.value] || 'warning.main'}
            sx={{ width: '100%' }}
          />
        );
      } else {
        return (
          <Box
            sx={{
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              height: '100%',
            }}
          >
            <Tooltip
              title={`${params.row.bookedHours} von ${params.row.plannedHours} Stunden gebucht`}
            >
              <LinearProgress
                variant="determinate"
                value={(params.row.bookedHours / params.row.plannedHours) * 100}
                sx={{
                  width: '100%',
                  height: '32px',
                  borderRadius: '4px',
                  display: 'flex',
                  alignSelf: 'center',
                }}
              />
            </Tooltip>
          </Box>
        );
      }
    },
    filterable: false,
    searchable: false,
    sortable: false,
    disableColumnMenu: true,
  },
  {
    field: 'owner',
    headerName: 'Projektleitung',
    width: 200,
    renderCell: (params) => {
      const owner = params.value;
      return (
        <Box display="flex" alignItems="center">
          <Avatar
            src={owner?.profilePictureUrl}
            sx={{ width: 30, height: 30, mr: 1 }}
          />
          {owner ? `${owner.firstName} ${owner.lastName}` : 'Unbekannt'}
        </Box>
      );
    },
    filterable: false,
    searchable: false,
    sortable: false,
    disableColumnMenu: true,
  },

  {
    field: 'team',
    headerName: 'Projektteam',
    width: 120,
    renderCell: (params) => {
      console.log(params);
      const team = params.value;
      if (!team || team.length === 0) return <span>–</span>;
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', height: '100%' }}>
          <AvatarGroup max={4} spacing={24}>
            {team.map((member) => (
              <Tooltip
                key={member._id}
                title={`${member.firstName} ${member.lastName}`}
              >
                <Avatar
                  src={member.profilePictureUrl}
                  sx={{ width: 30, height: 30, mr: 1 }}
                />
              </Tooltip>
            ))}
          </AvatarGroup>
        </Box>
      );
    },
    filterable: false,
    searchable: false,
    sortable: false,
    disableColumnMenu: true,
  },
];

export default function ProjectsPage() {
  const { users } = useRouteLoaderData('auth');
  const [statusFilter, setStatusFilter] = useState(null);
  const [teamLead, setTeamLead] = useState(null);
  const [party, setParty] = useState(null);
  const navigate = useNavigate();

  const teamLeads = useMemo(() => {
    return users.filter((user) => projects.some((project) => project.owner === user._id));
  }, [users]);

  const populatedProjects = projects.map((project) => {
    const owner = users.find((user) => user._id === project.owner);
    const team = project.team.map((memberId) => users.find((user) => user._id === memberId));
    return {
      ...project,
      owner,
      team,
    };
  });

  return (
    <Box
      sx={{
        display: 'flex',
        maxWidth: '100%',
        width: '1600px',
        justifyContent: 'center',
        flexDirection: 'row',
        alignSelf: 'center',
        justifySelf: 'center',
        p: 2,
        gap: 2,
      }}
    >
      <Card sx={{ p: 2, width: '300px', height: 'fit-content' }}>
        <PartySelector customer={true} party={party} setParty={setParty} />
        <Box sx={{ mt: 1 }}>
          <UserSelector user={teamLead} setUser={setTeamLead} options={teamLeads} />
        </Box>
        <Box
          className="status-selector"
          sx={{ mt: 1, display: 'flex', flexDirection: 'row', gap: 1 }}
        >
          <StatusFilter
            status={statusFilter}
            setStatus={setStatusFilter}
            name="Geplant"
            color="warning"
          />
          <StatusFilter
            status={statusFilter}
            setStatus={setStatusFilter}
            name="Aktiv"
            color="success"
          />
          <StatusFilter
            status={statusFilter}
            setStatus={setStatusFilter}
            name="Abgeschlossen"
            color="info"
          />
        </Box>
      </Card>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'row', gap: 2 }}>
          <Kpi
            title="Verzögerte Projekte"
            value={10}
            Icon={ScheduleOutlined}
            color="error.main"
          />
          <Kpi title="Laufende Projekte" value={5} Icon={TrendingUp} />
          <Kpi
            title="Geplante Projekte"
            value={3}
            Icon={Event}
            color="warning.main"
          />
        </Box>
        <ListCard
          rows={populatedProjects}
          columns={columns}
          checkboxSelection={false}
          onRowClick={(row) => navigate(`/projects/${row.id}`)}
        />
      </Box>
    </Box>
  );
}
